import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack } from '@/components/ui';

import type { ISearchOptions, SearchAddon } from '@xterm/addon-search';

interface TerminalFindBarProps {
  search: SearchAddon | null;
  onClose: () => void;
}

/**
 * Inline find for the terminal scrollback, shown in the dock header. Enter / shift+
 * Enter step through matches, Escape closes. Decorations come from
 * @xterm/addon-search (needs allowProposedApi on the Terminal).
 */
export function TerminalFindBar({ search, onClose }: TerminalFindBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<{
    index: number;
    count: number;
  } | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Match counts arrive asynchronously as the addon walks the buffer.
  useEffect(() => {
    if (!search) return;
    const sub = search.onDidChangeResults(({ resultIndex, resultCount }) => {
      setResult(
        resultCount > 0 ? { index: resultIndex, count: resultCount } : null
      );
    });
    return () => sub.dispose();
  }, [search]);

  const options: ISearchOptions = {
    incremental: true,
    decorations: {
      matchOverviewRuler: '#e5c890',
      activeMatchColorOverviewRuler: '#e78284',
      matchBackground: '#e5c89055',
      activeMatchBackground: '#e7828488',
    },
  };

  const step = (direction: 1 | -1) => {
    if (!search || !query) return;
    if (direction === 1) search.findNext(query, options);
    else search.findPrevious(query, options);
  };

  return (
    <XStack className="items-center gap-1">
      <input
        ref={inputRef}
        value={query}
        placeholder="Find"
        aria-label="Find in terminal"
        spellCheck={false}
        className="h-6 w-48 rounded border border-border bg-background px-2 font-mono text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-accent"
        onChange={(event) => {
          const next = event.target.value;
          setQuery(next);
          if (!search) return;
          if (next) search.findNext(next, options);
          else {
            search.clearDecorations();
            setResult(null);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            step(event.shiftKey ? -1 : 1);
          }
        }}
      />
      <span className="w-14 text-right text-xs tabular-nums text-foreground-subtle">
        {query ? (result ? `${result.index + 1}/${result.count}` : '0/0') : ''}
      </span>
      <IconButton
        size="sm"
        icon={Icons.chevronUp}
        aria-label="Previous match"
        tooltip="Previous match"
        onClick={() => step(-1)}
      />
      <IconButton
        size="sm"
        icon={Icons.chevronDown}
        aria-label="Next match"
        tooltip="Next match"
        onClick={() => step(1)}
      />
    </XStack>
  );
}
